import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilterContext } from '@/context';
import { readShowIdsFromParam } from '@/utils/routeUtils';

/**
 * Result of {@link useSelectedShows}.
 */
export interface UseSelectedShowsResult {
  /** Show ids parsed from `?shows=`, in URL order, deduped and capped at `maxShows`. */
  selectedShowIds: string[];
  /**
   * Replaces the selection by writing `?shows=` (other query params are preserved, history is
   * replaced). The incoming ids are deduped and capped at `maxShows`. If multi-show mode is active, clearing the selection
   * (passing an empty array) removes the `shows` param entirely, which is read back as "all shows".
   */
  setSelectedShowIds: (showIds: string[]) => void;
  /** `true` while fewer than `maxShows` shows are selected. */
  canSelectMore: boolean;
}

/**
 * Reads and writes the multi-show content performance selection via the `?shows=` query param
 * (comma-separated show ids).
 *
 * Only active in query-param multi-show mode. When inactive, the selection is always empty and
 * writes are no-ops. Mirrors the parsed ids into `filters.selectedShows` while active.
 * @param isMultiShowMode - Whether the `?shows=` multi-show mode is active.
 * @param maxShows - Maximum number of shows that can be selected at once.
 * @returns The current selection plus a setter and capacity flag.
 */
const useSelectedShows = (isMultiShowMode: boolean, maxShows: number): UseSelectedShowsResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilters } = useFilterContext();
  const showsParam = searchParams.get('shows');

  const selectedShowIds = useMemo(
    () => (isMultiShowMode ? readShowIdsFromParam(showsParam, maxShows) : []),
    [isMultiShowMode, showsParam, maxShows]
  );

  useEffect(() => {
    if (!isMultiShowMode) return;

    setFilters((prev) => ({
      ...prev,
      selectedShows: selectedShowIds,
    }));
  }, [isMultiShowMode, selectedShowIds, setFilters]);

  const setSelectedShowIds = useCallback(
    (showIds: string[]) => {
      if (!isMultiShowMode) return;

      const nextIds = [...new Set(showIds)].slice(0, maxShows);

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextIds.length === 0) {
            next.delete('shows');
          } else {
            next.set('shows', nextIds.join(','));
          }
          return next;
        },
        { replace: true }
      );
    },
    [isMultiShowMode, maxShows, setSearchParams]
  );

  return {
    selectedShowIds,
    setSelectedShowIds,
    canSelectMore: selectedShowIds.length < maxShows,
  };
};

export default useSelectedShows;
